import type { StatusTone } from '../contracts/common';
import type {
  AttachmentStatus,
  ClientMatchStatus,
  EmailDraftApprovalState,
  EmailDraftAuthorType,
  EmailDraftStatus,
  EmailSendBlockReason,
  MailboxStatus,
  MailThreadFilter,
  MessageDirection,
  RecipientCheckStatus,
  SyncRunStatus,
  SyncRunTrigger,
} from '../contracts/mail';

/** Dopasowanie klienta do wiadomości (baner Figma 02: „Nieznany nadawca. Przypisz do klienta…”). */
export const CLIENT_MATCH_LABELS: Record<ClientMatchStatus, string> = {
  matched: 'Przypisany',
  ambiguous: 'Niejednoznaczny',
  unknown: 'Nierozpoznany',
  manual: 'Ręcznie',
};

export const CLIENT_MATCH_TONES: Record<ClientMatchStatus, StatusTone> = {
  matched: 'neutral',
  ambiguous: 'warning',
  unknown: 'warning',
  manual: 'neutral',
};

/** Baner nad wiadomością bez klienta (Figma 02). */
export const CLIENT_MATCH_BANNERS: Record<'ambiguous' | 'unknown', string> = {
  unknown: 'Nieznany nadawca. Przypisz do klienta albo dodaj nowego.',
  ambiguous: 'Ten adres jest kontaktem kilku klientów. Wybierz, którego dotyczy wiadomość.',
};

/** Wątek samych „Mniej ważnych” bez klienta (newsletter, automat) — spokojna linia zamiast banera (przegląd 17.10.2026). */
export const CLIENT_MATCH_QUIET: Record<'ambiguous' | 'unknown', string> = {
  unknown: 'Nadawca spoza klientów.',
  ambiguous: 'Ten adres jest kontaktem kilku klientów.',
};

export const MESSAGE_DIRECTION_LABELS: Record<MessageDirection, string> = {
  in: 'Odebrana',
  out: 'Wysłana',
};

export const MAILBOX_STATUS_LABELS: Record<MailboxStatus, string> = {
  active: 'Synchronizacja działa',
  paused: 'Wstrzymana',
  error: 'Błąd synchronizacji',
};

export const EMAIL_DRAFT_STATUS_LABELS: Record<EmailDraftStatus, string> = {
  draft: 'Szkic',
  pending: 'Czeka na akceptację',
  sent: 'Wysłany',
  failed: 'Nie wysłano',
  cancelled: 'Anulowany',
};

export const EMAIL_DRAFT_STATUS_TONES: Record<EmailDraftStatus, StatusTone> = {
  draft: 'neutral',
  pending: 'accent',
  sent: 'success',
  failed: 'danger',
  cancelled: 'neutral',
};

export const EMAIL_DRAFT_AUTHOR_LABELS: Record<EmailDraftAuthorType, string> = {
  user: 'Ręcznie',
  ai: 'Copilot',
};

export const SYNC_RUN_STATUS_LABELS: Record<SyncRunStatus, string> = {
  running: 'Trwa',
  succeeded: 'Zakończona',
  failed: 'Błąd',
};

export const SYNC_RUN_TRIGGER_LABELS: Record<SyncRunTrigger, string> = {
  schedule: 'Automatycznie',
  manual: 'Ręcznie',
  backfill: 'Pobieranie starszych maili',
};

export const ATTACHMENT_STATUS_LABELS: Record<AttachmentStatus, string> = {
  pending: 'Do pobrania',
  stored: 'Zapisany',
  skipped: 'Pominięty',
};

export const EMAIL_SEND_BLOCK_REASON_LABELS: Record<EmailSendBlockReason, string> = {
  no_recipients: 'Podaj odbiorcę.',
  ambiguous_recipient: 'Adres odbiorcy jest kontaktem kilku klientów. Rozstrzygnij konflikt w profilu klienta.',
  recipient_other_client: 'Odbiorca jest kontaktem innego klienta niż ta wiadomość.',
  no_client: 'Przypisz wiadomość do klienta przed wysłaniem.',
  no_mailbox: 'Brak skrzynki do wysyłki. Podłącz pocztę w Ustawieniach → Integracje.',
  mailbox_unavailable: 'Skrzynka nie działa (utracono autoryzację albo błąd). Połącz ją ponownie.',
};

/** Odbiorca w kompozytorze: ostrzeżenie i blokada wysyłki do jawnego potwierdzenia (Etap 5). */
export const RECIPIENT_CHECK_LABELS: Record<RecipientCheckStatus, string> = {
  ok: 'Kontakt klienta',
  new_address: 'Adres spoza kontaktów klienta',
  ambiguous: 'Adres jest kontaktem kilku klientów',
  other_client: 'Adres jest kontaktem innego klienta',
};

export const MAIL_THREAD_FILTER_LABELS: Record<MailThreadFilter, string> = {
  all: 'Wszystkie',
  unassigned: 'Do przypisania',
};

/** Stan akceptacji szkicu (wiersz szkicu w wątku). */
export const EMAIL_DRAFT_APPROVAL_LABELS: Record<Exclude<EmailDraftApprovalState, null>, string> = {
  pending: 'Czeka na akceptację',
  approved: 'Zatwierdzony, wysyłanie…',
  executing: 'Wysyłanie…',
  succeeded: 'Wysłany',
  failed: 'Nie wysłano',
  rejected: 'Odrzucony',
  expired: 'Wygasł',
};
